import type { EventHandler } from "../../interfaces/EventHandler";

/**
 * 基础事件映射表类型。
 * 用户可以扩展此类型来定义具体的事件及其数据类型，例如：
 * interface MyCustomEvents extends BaseEventMap {
 *   'userLogin': { userId: string; timestamp: Date };
 *   'itemAdded': { itemId: number; quantity: number };
 *   'noDataEvent': void; // 对于没有数据的事件，使用 void 类型
 * }
 *
 * // 使用示例:
 * // class MyEmitter extends EventEmitter<MyCustomEvents> {}
 * // const emitter = new MyEmitter();
 * // emitter.on('userLogin', (data) => { console.log(data.userId); }); // data 类型为 { userId: string; timestamp: Date }
 * // emitter.emit('itemAdded', { itemId: 1, quantity: 10 });
 * // emitter.emit('noDataEvent', undefined); // void 类型的事件数据需要显式传递 undefined
 */
export type BaseEventMap = Record<string, any>;

/**
 * 事件发射器基类
 * 提供事件注册、移除和触发的基本功能。
 * 通过泛型 M (事件映射表) 来实现事件名称和事件数据的类型安全。
 * @template M 一个映射表，其键是事件名称 (字符串)，其值是该事件对应的数据类型。
 *             默认为 BaseEventMap，允许任何字符串事件名和 any 类型的数据。
 */
export class EventEmitter<M extends BaseEventMap = BaseEventMap> {
    // 事件监听器映射。
    // 内部存储使用 string 作为键，值为对应事件处理函数数组。
    // 类型安全通过公共方法签名来保证。
    private _eventListeners: Map<string, Array<EventHandler<any>>> = new Map();

    /**
     * 添加事件监听器。
     * @param eventName 事件类型，必须是 M 中的一个键。
     * @param handler 事件处理函数，它接收特定于此事件类型的数据 (M[K])。
     */
    public on<K extends keyof M>(eventName: K, handler: EventHandler<M[K]>): this {
        const eventKey = eventName as string; // K 是 M 的键，M 继承自 Record<string, any>，所以 K 是字符串兼容的
        if (!this._eventListeners.has(eventKey)) {
            this._eventListeners.set(eventKey, []);
        }
        // 将具体类型的 handler (EventHandler<M[K]>) 存入 EventHandler<any> 数组是安全的（向上转型）
        this._eventListeners.get(eventKey)!.push(handler as EventHandler<any>);
        return this;
    }

    /**
     * 添加只执行一次的事件监听器。
     * @param eventName 事件类型，必须是 M 中的一个键。
     * @param handler 事件处理函数，接收 M[K] 类型的数据。
     */
    public once<K extends keyof M>(eventName: K, handler: EventHandler<M[K]>): this {
        // 包装后的 onceHandler 保持了与原始 handler 相同的类型签名 EventHandler<M[K]>
        const onceHandler: EventHandler<M[K]> = (eventData) => {
            this.off(eventName, onceHandler); // 移除包装后的 handler
            handler(eventData);
        };
        // 将原始处理函数附加到包装器上，以便 off 方法能够通过它来移除监听器
        (onceHandler as any)._originalHandler = handler;
        this.on(eventName, onceHandler); // on 方法会正确处理类型
        return this;
    }

    /**
     * 移除事件监听器。
     * @param eventName 事件类型，必须是 M 中的一个键。
     * @param handler 事件处理函数。
     */
    public off<K extends keyof M>(eventName: K, handler: EventHandler<M[K]>): this {
        const eventKey = eventName as string;
        if (!this._eventListeners.has(eventKey)) return this;

        const handlers = this._eventListeners.get(eventKey)!;
        // 需要查找原始处理函数（如果是通过 once 添加的）或处理函数本身
        const index = handlers.findIndex(h => h === handler || (h as any)._originalHandler === handler);
        if (index !== -1) {
            handlers.splice(index, 1);
        }
        // 如果移除后没有处理函数了，删除该事件类型
        if (handlers.length === 0) {
            this._eventListeners.delete(eventKey);
        }
        return this;
    }

    /**
     * 触发事件。
     * @param eventName 事件类型，必须是 M 中的一个键。
     * @param eventData 事件数据，其类型应为 M[K]。
     */
    public emit<K extends keyof M>(eventName: K, eventData: M[K]): void {
        const eventKey = eventName as string;
        if (!this._eventListeners.has(eventKey)) return;

        // 复制一份处理函数数组，防止在回调中修改监听器列表导致问题
        // 从 _eventListeners 中获取的 handlers 数组元素类型是 EventHandler<any>
        // 但我们知道对于特定的 eventName K，它们实际上是 EventHandler<M[K]> (由 on 方法保证)
        // 因此，使用 M[K] 类型的 eventData 调用它们是类型安全的
        const handlersToExecute = [...this._eventListeners.get(eventKey)!];
        handlersToExecute.forEach(handler => handler(eventData));
    }

    /**
     * 移除指定类型的所有事件监听器。
     * @param eventName 事件类型 (M 中的键)，如果不提供则移除所有类型的监听器。
     */
    public removeAllListeners<K extends keyof M>(eventName?: K): this {
        if (eventName) {
            const eventKey = eventName as string;
            this._eventListeners.delete(eventKey);
        } else {
            this._eventListeners.clear();
        }
        return this;
    }
}